import type { Operation } from "@workspace/operations/types";

/**
 * HA-360 (planning ruling, HA-360 comment 8487) — the operator whose completion opened the receipt modal keeps the
 * operations that gate its Print Receipt button, for as long as that modal is open.
 *
 * Completing an order clears the current user (the till then goes to staff select), and with no user the operations
 * are empty, so the button would vanish the moment the modal opens. This snapshot is scoped to the receipt modal only:
 * it is taken when the modal opens, dropped when it closes, and dropped when a staff member is selected. It never feeds
 * the till's general operations, and the server still checks `epos:receipt:print` on the print itself.
 */
export type ReceiptOperationsEvent =
	| { type: "modalOpened"; operatorOperations: Operation[] }
	| { type: "modalClosed" }
	| { type: "staffSelected" };

/**
 * The snapshot after an event. Empty operator operations give no snapshot, so the button stays hidden (fail closed).
 */
export function applyReceiptOperations(
	event: ReceiptOperationsEvent,
): Operation[] | null {
	switch (event.type) {
		case "modalOpened":
			return event.operatorOperations.length > 0
				? event.operatorOperations
				: null;
		case "modalClosed":
		case "staffSelected":
			return null;
	}
}

/** The operator whose operations the receipt snapshot may draw on, and those operations. */
export type OperatorOperations = {
	operatorId: string | null;
	operations: Operation[];
};

/**
 * Tracks the operations of the staff member currently at the till, so the snapshot never carries another operator's.
 * Completing an order clears the current user; that keeps the completing operator's operations (the receipt needs them).
 * Selecting a different operator replaces them, and an empty list for a different operator is not inherited.
 */
export function trackOperator(
	previous: OperatorOperations,
	currentUserId: string | null | undefined,
	currentUserOperations: Operation[],
): OperatorOperations {
	if (!currentUserId) return previous;
	if (currentUserOperations.length > 0)
		return { operatorId: currentUserId, operations: currentUserOperations };
	if (previous.operatorId === currentUserId) return previous;
	return { operatorId: currentUserId, operations: [] };
}
